/**
 * card-timeline-store — interface com endpoints de atividade + comentários
 * de card (FEAT-V2-002 PR 1).
 *
 * Hooks expostos:
 *   - useCardActivity(cardId, workflow) — timeline paginada por cursor
 *   - useCardComments(cardId, workflow) — lista completa de comentários
 *   - useCommentActions() — create + edit + soft-delete + markRead
 *   - useNotifications({unread}) — lista in-app
 *   - useNotificationActions() — markRead + markAllRead
 *   - useUnreadNotificationsCount() — derived count pro badge
 *
 * Padrão React Query igual aos outros stores (api.get/post/patch/delete +
 * `hasToken` gate + invalidação por queryKey após mutations).
 */

import { useCallback, useState } from 'react';
import { useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';

import { api, hasToken } from './api';

// ─── Tipos ──────────────────────────────────────────────────────────────

export type CardActivityType =
  | 'created'
  | 'archived'
  | 'restored'
  | 'deleted'
  | 'stage_changed'
  | 'assignee_changed'
  | 'field_changed'
  | 'comment_added'
  | 'comment_edited'
  | 'comment_deleted';

export interface CardActivity {
  id: string;
  cardId: string;
  workflowSlug: string;
  tipo: CardActivityType | string;
  actorId: string | null;
  payload: Record<string, unknown>;
  criadoEmIso: string;
}

export interface ActivityPage {
  items: CardActivity[];
  nextCursor: string | null;
}

export interface CardComment {
  id: string;
  cardId: string;
  workflowSlug: string;
  autorId: string;
  corpo: string;
  mencoes: string[];
  criadoEmIso: string;
  editadoEmIso: string | null;
  deletadoEmIso: string | null;
  deletadoPorId: string | null;
  /** FEAT-V2-002 PR 2: reply/quote — null se comentário top-level. */
  replyToId: string | null;
  /** FEAT-V2-002 PR 3: pinado por admin? — render no topo. */
  pinned: boolean;
}

// ─── PR 2 (Alta) types ────────────────────────────────────────────────

export interface CommentAttachment {
  id: string;
  commentId: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadedBy: string;
  criadoEmIso: string;
}

export interface CommentReaction {
  id: string;
  commentId: string;
  userId: string;
  emoji: string;
  criadoEmIso: string;
}

export interface CommentRead {
  userId: string;
  lidaEmIso: string;
}

/** PR 3 (Média) — estado de watch (mute) por user no card. */
export interface CardWatchStatus {
  cardId: string;
  userId: string;
  ativo: boolean;
}

/** PR 4 — entry da lista de watchers ATIVOS do card (avatares no header). */
export interface CardWatcher {
  cardId: string;
  userId: string;
  ativo: boolean;
  criadoEmIso: string;
}

export interface NotificationItem {
  id: string;
  userId: string;
  type: string;
  payload: Record<string, unknown>;
  lida: boolean;
  criadoEmIso: string;
}

export interface CreateCommentInput {
  corpo: string;
  mencoes?: string[];
  /** FEAT-V2-002 PR 2: reply opcional. */
  replyToId?: string | null;
  /** BUG-B1.6.2: admin marca comentário como visível ao cliente no Portal.
   *  Default false (interno). Pra corretiva (chamados Portal) é comum
   *  o admin querer responder o cliente. */
  visivelCliente?: boolean;
}

// ─── Keys ───────────────────────────────────────────────────────────────

export const TIMELINE_KEYS = {
  activity: (cardId: string, workflow: string | null) =>
    ['v1', 'cards', cardId, 'activity', { workflow }] as const,
  comments: (cardId: string, workflow: string | null) =>
    ['v1', 'cards', cardId, 'comments', { workflow }] as const,
  notifications: (unread: boolean) => ['v1', 'notifications', { unread }] as const,
};

// ─── useCardActivity (infinite query) ───────────────────────────────────

export function useCardActivity(cardId: string, workflow: string | null = null) {
  return useInfiniteQuery({
    queryKey: TIMELINE_KEYS.activity(cardId, workflow),
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (workflow) params.set('workflow', workflow);
      if (pageParam) params.set('cursor', pageParam);
      const qs = params.toString();
      const url = qs
        ? `/api/v1/cards/${encodeURIComponent(cardId)}/activity?${qs}`
        : `/api/v1/cards/${encodeURIComponent(cardId)}/activity`;
      return api.get<ActivityPage>(url);
    },
    initialPageParam: '' as string,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!cardId && hasToken(),
    staleTime: 30_000,
  });
}

// ─── useCardComments ────────────────────────────────────────────────────

export function useCardComments(cardId: string, workflow: string | null = null) {
  return useQuery({
    queryKey: TIMELINE_KEYS.comments(cardId, workflow),
    queryFn: () => {
      const params = new URLSearchParams();
      if (workflow) params.set('workflow', workflow);
      const qs = params.toString();
      const url = qs
        ? `/api/v1/cards/${encodeURIComponent(cardId)}/comments?${qs}`
        : `/api/v1/cards/${encodeURIComponent(cardId)}/comments`;
      return api.get<CardComment[]>(url);
    },
    enabled: !!cardId && hasToken(),
    staleTime: 15_000,
  });
}

// ─── useCommentActions ─────────────────────────────────────────────────

export function useCommentActions() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidateCard = (cardId: string) => {
    qc.invalidateQueries({ queryKey: ['v1', 'cards', cardId] });
  };

  const createComment = useCallback(
    async (cardId: string, workflow: string, input: CreateCommentInput): Promise<CardComment> => {
      setPending('create');
      try {
        const c = await api.post<CardComment>(
          `/api/v1/cards/${encodeURIComponent(cardId)}/comments?workflow=${encodeURIComponent(workflow)}`,
          {
            corpo: input.corpo,
            mencoes: input.mencoes ?? [],
            replyToId: input.replyToId ?? null,
          }
        );
        invalidateCard(cardId);
        // Notifs invalidam pra mencionados verem badge novo
        qc.invalidateQueries({ queryKey: ['v1', 'notifications'] });
        return c;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  const editComment = useCallback(
    async (commentId: string, cardId: string, corpo: string): Promise<CardComment> => {
      setPending(commentId);
      try {
        const c = await api.patch<CardComment>(`/api/v1/comments/${encodeURIComponent(commentId)}`, {
          corpo,
        });
        invalidateCard(cardId);
        return c;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  const deleteComment = useCallback(
    async (commentId: string, cardId: string): Promise<void> => {
      setPending(commentId);
      try {
        await api.delete(`/api/v1/comments/${encodeURIComponent(commentId)}`);
        invalidateCard(cardId);
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  const markRead = useCallback(async (commentId: string): Promise<void> => {
    // Silent — não muda pending nem invalida (UI já reflete via state local)
    await api.post(`/api/v1/comments/${encodeURIComponent(commentId)}/read`);
  }, []);

  return { createComment, editComment, deleteComment, markRead, pending };
}

// ─── useNotifications ───────────────────────────────────────────────────
// Reusa endpoint /api/v1/notifications existente (migration 001).
// Schema retornado: `{id, userId, type, payload, lida (boolean), criadoEmIso}`

export function useNotifications(opts: { unread?: boolean; pollMs?: number } = {}) {
  const { unread = false, pollMs = 30_000 } = opts;
  return useQuery({
    queryKey: TIMELINE_KEYS.notifications(unread),
    queryFn: () => {
      const url = unread ? '/api/v1/notifications?unread=1' : '/api/v1/notifications';
      return api.get<NotificationItem[]>(url);
    },
    enabled: hasToken(),
    staleTime: pollMs / 2,
    refetchInterval: pollMs,
    refetchOnWindowFocus: true,
  });
}

export function useUnreadNotificationsCount(): number {
  const { data = [] } = useNotifications({ unread: true });
  return data.length;
}

// ─── PR 2 (Alta) hooks ────────────────────────────────────────────────

export function useCommentAttachments(commentId: string | null) {
  return useQuery({
    queryKey: ['v1', 'comments', commentId, 'attachments'] as const,
    queryFn: () =>
      api.get<CommentAttachment[]>(`/api/v1/comments/${encodeURIComponent(commentId!)}/attachments`),
    enabled: !!commentId && hasToken(),
    staleTime: 30_000,
  });
}

export function useCommentReactions(commentId: string | null) {
  return useQuery({
    queryKey: ['v1', 'comments', commentId, 'reactions'] as const,
    queryFn: () =>
      api.get<CommentReaction[]>(`/api/v1/comments/${encodeURIComponent(commentId!)}/reactions`),
    enabled: !!commentId && hasToken(),
    staleTime: 15_000,
  });
}

export function useCommentReads(commentId: string | null) {
  return useQuery({
    queryKey: ['v1', 'comments', commentId, 'reads'] as const,
    queryFn: () =>
      api.get<CommentRead[]>(`/api/v1/comments/${encodeURIComponent(commentId!)}/reads`),
    enabled: !!commentId && hasToken(),
    staleTime: 60_000,
  });
}

export function useAttachmentActions() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const upload = useCallback(
    async (commentId: string, files: File[]): Promise<CommentAttachment[]> => {
      setPending(commentId);
      try {
        const form = new FormData();
        for (const f of files) form.append('files', f);
        // Não usa api.post — multipart precisa skip de Content-Type pra browser definir boundary
        const token = localStorage.getItem('igm_jwt');
        const r = await fetch(`/api/v1/comments/${encodeURIComponent(commentId)}/attachments`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: form,
        });
        if (!r.ok) {
          const errBody = await r.json().catch(() => ({}));
          throw new Error((errBody as { error?: string }).error ?? `HTTP ${r.status}`);
        }
        const created = (await r.json()) as CommentAttachment[];
        qc.invalidateQueries({ queryKey: ['v1', 'comments', commentId, 'attachments'] });
        return created;
      } finally {
        setPending(null);
      }
    },
    [qc]
  );

  const remove = useCallback(
    async (attachmentId: string, commentId: string): Promise<void> => {
      setPending(attachmentId);
      try {
        await api.delete(`/api/v1/attachments/${encodeURIComponent(attachmentId)}`);
        qc.invalidateQueries({ queryKey: ['v1', 'comments', commentId, 'attachments'] });
      } finally {
        setPending(null);
      }
    },
    [qc]
  );

  return { upload, remove, pending };
}

// ─── PR 3 (Média) hooks ───────────────────────────────────────────────

export function useCardWatch(cardId: string | null) {
  return useQuery({
    queryKey: ['v1', 'cards', cardId, 'watch'] as const,
    queryFn: () => api.get<CardWatchStatus>(`/api/v1/cards/${encodeURIComponent(cardId!)}/watch`),
    enabled: !!cardId && hasToken(),
    staleTime: 30_000,
  });
}

export function useCardWatchers(cardId: string | null) {
  return useQuery({
    queryKey: ['v1', 'cards', cardId, 'watchers'] as const,
    queryFn: () => api.get<CardWatcher[]>(`/api/v1/cards/${encodeURIComponent(cardId!)}/watchers`),
    enabled: !!cardId && hasToken(),
    staleTime: 30_000,
  });
}

export function useCardWatchActions() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const toggleWatch = useCallback(
    async (cardId: string, ativo: boolean): Promise<void> => {
      setPending(cardId);
      try {
        await api.put(`/api/v1/cards/${encodeURIComponent(cardId)}/watch`, { ativo });
        qc.invalidateQueries({ queryKey: ['v1', 'cards', cardId, 'watch'] });
      } finally {
        setPending(null);
      }
    },
    [qc]
  );

  return { toggleWatch, pending };
}

export function usePinComment() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const setPinned = useCallback(
    async (commentId: string, cardId: string, pinned: boolean): Promise<void> => {
      setPending(commentId);
      try {
        await api.put(`/api/v1/comments/${encodeURIComponent(commentId)}/pin`, { pinned });
        qc.invalidateQueries({ queryKey: ['v1', 'cards', cardId] });
      } finally {
        setPending(null);
      }
    },
    [qc]
  );

  return { setPinned, pending };
}

export function useReactionActions() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const toggle = useCallback(
    async (commentId: string, emoji: string, isAdding: boolean): Promise<void> => {
      const key = `${commentId}:${emoji}`;
      setPending(key);
      try {
        if (isAdding) {
          try {
            await api.post(`/api/v1/comments/${encodeURIComponent(commentId)}/reactions`, { emoji });
          } catch (err) {
            // 409 já existe — tratar como no-op
            if (err instanceof Error && err.message.includes('409')) {
              /* swallow */
            } else throw err;
          }
        } else {
          await api.delete(`/api/v1/comments/${encodeURIComponent(commentId)}/reactions`, {
            body: { emoji },
          });
        }
        qc.invalidateQueries({ queryKey: ['v1', 'comments', commentId, 'reactions'] });
      } finally {
        setPending(null);
      }
    },
    [qc]
  );

  return { toggle, pending };
}

export function useNotificationActions() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['v1', 'notifications'] });

  const markRead = useCallback(async (notifId: string): Promise<void> => {
    setPending(notifId);
    try {
      await api.put(`/api/v1/notifications/${encodeURIComponent(notifId)}/read`);
      invalidate();
    } finally {
      setPending(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc]);

  const markAllRead = useCallback(async (): Promise<void> => {
    setPending('all');
    try {
      await api.put('/api/v1/notifications/read-all');
      invalidate();
    } finally {
      setPending(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc]);

  return { markRead, markAllRead, pending };
}
